import { formatMoney } from '@theme/money-formatting';

const STEP_COUNT = 6;
const OPTION_NAMES = ['Frame Colour', 'Mini Frames Colour', 'Background'];

/**
 * @typedef {{ id: number, options: string[], price: number, available: boolean, image: string | null }} Variant
 * @typedef {{ key: string, label: string, subtitle?: string | null, image: string | null, altImages: { alt: string, image: string }[],
 *   optionNames: string[], variants: Variant[] }} Model
 * @typedef {{ id: number, label: string, price: number, image: string | null }} Extra
 * @typedef {{ figures?: { id: number } | null, frames?: { id: number, value: string, price?: number, image: string | null }[],
 *   frameVariantFor?: Record<string, string | null>, listFor?: Record<string, string | null> }} Gifts
 */

/**
 * Step-by-step museum configurator. Renders option cards from the JSON data emitted by
 * sections/museum-builder.liquid and adds the chosen variant plus extras to the cart.
 */
class MuseumBuilder extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /**
   * @type {{ labels: Record<string, any>, pricing: { markup?: string | null, miniValue?: string | null }, models: Model[],
   *   extras: Extra[], gifts: Gifts }}
   */
  #data = { labels: {}, pricing: {}, models: [], extras: [], gifts: {} };
  #step = 0;
  #furthest = 0;
  #state = this.#emptyState();

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const script = this.querySelector('script[data-museum-builder-data]');
    try {
      const parsed = JSON.parse(script?.textContent || '{}');
      this.#data = {
        labels: parsed.labels || {},
        pricing: parsed.pricing || {},
        models: parsed.models || [],
        extras: parsed.extras || [],
        gifts: parsed.gifts || {},
      };
    } catch {
      this.#data = { labels: {}, pricing: {}, models: [], extras: [], gifts: {} };
    }

    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.addEventListener('change', (event) => this.handleChange(event), { signal });

    for (const link of document.querySelectorAll(`[data-museum-builder-start="${this.id}"]`)) {
      link.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          history.replaceState(history.state, '', '#MuseumBuilder');
          this.reveal();
        },
        { signal }
      );
    }
    window.addEventListener('hashchange', () => this.#revealFromHash(), { signal });

    this.#render();
    this.#revealFromHash();
    this.#startCountdown(signal);
  }

  /**
   * Counts down to 23:59:59 on the visitor's own clock (so their own time zone) and starts over at midnight.
   * @param {AbortSignal} signal
   */
  #startCountdown(signal) {
    const outputs = this.querySelectorAll('[data-countdown]');
    if (!outputs.length) return;

    const tick = () => {
      const now = new Date();
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      const seconds = Math.max(0, Math.floor((end.getTime() - now.getTime()) / 1000));
      const text = [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]
        .map((part) => String(part).padStart(2, '0'))
        .join(':');
      for (const output of outputs) output.textContent = text;
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    signal.addEventListener('abort', () => window.clearInterval(interval));
  }

  /** Shows the steps (hidden on load), hides the intro (heading, images, benefits, start button) and scrolls up. */
  reveal() {
    const body = this.querySelector('[data-builder-body]');
    if (body instanceof HTMLElement) body.hidden = false;
    if (!this.hasAttribute('data-design-mode')) {
      for (const element of this.querySelectorAll('[data-intro]')) {
        if (element instanceof HTMLElement) element.hidden = true;
      }
    }

    requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      const heading = this.querySelector(`[data-step="${this.#step}"] [data-step-heading]`);
      if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
    });
  }

  /** Opening the page with #MuseumBuilder (or this builder's own id) shows the builder straight away. */
  #revealFromHash() {
    if (window.location.hash.startsWith('#MuseumBuilder')) this.reveal();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const stepButton = event.target.closest('[data-go-step]');
    if (stepButton instanceof HTMLButtonElement) {
      this.#goTo(Number(stepButton.dataset.goStep));
      return;
    }

    const actionButton = event.target.closest('[data-action]');
    if (!(actionButton instanceof HTMLButtonElement)) return;

    switch (actionButton.dataset.action) {
      case 'next':
        if (this.#canContinue(this.#step)) this.#goTo(this.#step + 1);
        break;
      case 'back':
        this.#goTo(this.#step - 1);
        break;
      case 'checkout':
        this.#checkout(actionButton);
        break;
      case 'reset':
        this.#reset();
        break;
    }
  }

  /** @param {Event} event */
  handleChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.field) return;

    const field = input.dataset.field;
    if (field === 'extras') {
      const id = Number(input.value);
      if (input.checked) this.#state.extras.add(id);
      else this.#state.extras.delete(id);
    } else if (field === 'model' || field === 'colour' || field === 'mini' || field === 'background') {
      this.#state[field] = input.value;
      this.#sanitize();
    }

    this.#render({ keepOptions: field === 'extras' });
    if (field === 'model') this.#revealPriceBar();

    // Option cards are rebuilt on render; return focus to the matching input for keyboard users.
    if (!input.isConnected) {
      const replacement = [...this.querySelectorAll(`input[data-field="${field}"]`)].find(
        (candidate) => candidate instanceof HTMLInputElement && candidate.value === input.value
      );
      if (replacement instanceof HTMLInputElement) replacement.focus();
    }
  }

  #emptyState() {
    return {
      /** @type {string | null} */ model: null,
      /** @type {string | null} */ colour: null,
      /** @type {string | null} */ mini: null,
      /** @type {string | null} */ background: null,
      /** @type {Set<number>} */ extras: new Set(),
    };
  }

  /* ---------- Data helpers ---------- */

  /** @returns {Model | undefined} */
  get #model() {
    return this.#data.models.find((model) => model.key === this.#state.model);
  }

  /**
   * Option positions are looked up by name so the builder keeps working if the option order changes.
   * @param {Model} model
   */
  #positions(model) {
    const names = model.optionNames || [];
    return OPTION_NAMES.map((name, index) => {
      const position = names.indexOf(name);
      if (position !== -1) return position;
      // Mini frames can be a separate free gift product instead of a frame option.
      if (name === OPTION_NAMES[1] && names.length < OPTION_NAMES.length) return -1;
      return index;
    });
  }

  /**
   * True when the mini frames choice comes from the free gift product (its Gold/Silver variants) instead of a
   * frame option: whenever that product exists, or when the frame product has no mini frames option.
   * @param {Model} model
   */
  #miniFromGift(model) {
    return (this.#data.gifts.frames || []).length > 0 || this.#positions(model)[1] === -1;
  }

  /**
   * Available variants that match the given partial selection.
   * @param {Model | undefined} model
   * @param {{ colour?: string | null, mini?: string | null, background?: string | null }} selection
   */
  #matching(model, selection) {
    if (!model) return [];
    const [colourPos, miniPos, backgroundPos] = this.#positions(model);
    // With mini frames as a free gift, the frame variant no longer depends on the mini frames colour.
    const ignoreMini = this.#miniFromGift(model);
    return model.variants.filter(
      (variant) =>
        variant.available &&
        (!selection.colour || variant.options[colourPos] === selection.colour) &&
        (!selection.mini || ignoreMini || variant.options[miniPos] === selection.mini) &&
        (!selection.background || variant.options[backgroundPos] === selection.background)
    );
  }

  /**
   * Unique option values (in product order) with the first image found for each value.
   * @param {Variant[]} variants
   * @param {number} position
   */
  #values(variants, position) {
    /** @type {Map<string, { value: string, image: string | null, prices: number[] }>} */
    const values = new Map();
    for (const variant of variants) {
      const value = variant.options[position];
      const entry = values.get(value) || { value, image: null, prices: [] };
      entry.image = entry.image || variant.image;
      entry.prices.push(variant.price);
      values.set(value, entry);
    }
    return [...values.values()];
  }

  /**
   * Product image whose alt text is "<prefix> <value>" (any case); variant images are reserved for backgrounds.
   * @param {Model} model
   * @param {string} prefix
   * @param {string} value
   */
  #altImage(model, prefix, value) {
    const alt = `${prefix} ${value}`.toLowerCase();
    return (model.altImages || []).find((altImage) => altImage.alt.trim().toLowerCase() === alt)?.image ?? null;
  }

  #colourValues() {
    const model = this.#model;
    if (!model) return [];
    return this.#values(this.#matching(model, {}), this.#positions(model)[0]).map((entry) => ({
      ...entry,
      image: this.#altImage(model, 'Frame:', entry.value),
    }));
  }

  #miniValues() {
    const model = this.#model;
    if (!model || !this.#state.colour) return [];
    if (this.#miniFromGift(model)) {
      // One option per frame size: the FREE MINI FRAMES variant set for this frame in the section settings.
      const frame = this.#giftFrameFor(model);
      if (!frame) return [];
      return [
        {
          value: frame.value,
          image:
            this.#altImage(model, 'Mini frames:', frame.value) ?? frame.image ?? this.#altImage(model, 'Mini frames:', 'Gold'),
          prices: [0],
          price: frame.price || 0,
        },
      ];
    }
    return this.#values(this.#matching(model, { colour: this.#state.colour }), this.#positions(model)[1]).map(
      (entry) => ({ ...entry, image: this.#altImage(model, 'Mini frames:', entry.value) })
    );
  }

  #backgroundValues() {
    const model = this.#model;
    if (!model || !this.#state.colour || !this.#state.mini) return [];
    const variants = this.#matching(model, { colour: this.#state.colour, mini: this.#state.mini });
    return this.#values(variants, this.#positions(model)[2]).map((entry) => ({ ...entry, price: entry.prices[0] }));
  }

  /** @returns {Variant | undefined} */
  get #selectedVariant() {
    const { colour, mini, background } = this.#state;
    if (!colour || !mini || !background) return undefined;
    return this.#matching(this.#model, { colour, mini, background })[0];
  }

  /** Drops choices that are no longer available after an earlier choice changed. */
  #sanitize() {
    if (!this.#colourValues().some((entry) => entry.value === this.#state.colour)) this.#state.colour = null;
    if (!this.#miniValues().some((entry) => entry.value === this.#state.mini)) this.#state.mini = null;
    if (!this.#backgroundValues().some((entry) => entry.value === this.#state.background)) {
      this.#state.background = null;
    }
    this.#furthest = Math.min(this.#furthest, this.#firstIncompleteStep());
  }

  #firstIncompleteStep() {
    for (let step = 0; step < 4; step++) {
      if (!this.#canContinue(step)) return step;
    }
    return STEP_COUNT - 1;
  }

  /** @param {number} step */
  #canContinue(step) {
    switch (step) {
      case 0:
        return Boolean(this.#model);
      case 1:
        return Boolean(this.#state.colour);
      case 2:
        return Boolean(this.#state.mini);
      case 3:
        return Boolean(this.#selectedVariant);
      default:
        return true;
    }
  }

  #extrasTotal() {
    let total = 0;
    for (const extra of this.#data.extras) {
      if (this.#state.extras.has(extra.id)) total += extra.price;
    }
    return total;
  }

  /** Running total: chosen variant, or the cheapest match for the choices so far, plus extras. */
  #total() {
    const { colour, mini, background } = this.#state;
    const prices = this.#matching(this.#model, { colour, mini, background }).map((variant) => variant.price);
    const base = this.#selectedVariant?.price ?? (prices.length ? Math.min(...prices) : 0);
    return base + this.#extrasTotal();
  }

  /**
   * Section settings are euro amounts as text ("7,95" or "7.95"); prices in the data are in cents.
   * @param {'markup' | 'miniValue'} key
   */
  #pricingCents(key) {
    const value = Number(String(this.#data.pricing[key] ?? '').trim().replace(/[^\d,.]/g, '').replace(',', '.'));
    return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : 0;
  }

  /**
   * The FREE MINI FRAMES variant offered with this frame: the variant named in the section settings for the
   * frame (square = closed, rectangular = open), or else the first (square) / last (rectangular) gold variant.
   * @param {Model | undefined} model
   */
  #giftFrameFor(model) {
    const frames = this.#data.gifts.frames || [];
    if (!model || !frames.length) return undefined;
    const wanted = String(this.#data.gifts.frameVariantFor?.[model.key] || '').trim().toLowerCase();
    const exact = frames.find((frame) => frame.value.trim().toLowerCase() === wanted);
    if (exact) return exact;
    const gold = frames.filter((frame) => /gold/i.test(frame.value));
    const pool = gold.length ? gold : frames;
    return model.key === 'open' ? pool[pool.length - 1] : pool[0];
  }

  /** Value of the free mini frames in cents: the gift variant's own price, else the section setting. */
  #miniFramesValue() {
    return this.#giftFrameFor(this.#model)?.price || this.#pricingCents('miniValue');
  }

  /** Struck-through price: total plus the markup, plus the free mini frames' value from step 3 on. */
  #compareAtTotal() {
    return this.#total() + this.#pricingCents('markup') + (this.#step >= 2 ? this.#miniFramesValue() : 0);
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }

  /* ---------- Navigation ---------- */

  /** @param {number} step */
  #goTo(step) {
    const target = Math.max(0, Math.min(step, STEP_COUNT - 1));
    if (target > this.#furthest + 1) return;
    for (let index = 0; index < target; index++) {
      if (!this.#canContinue(index)) return;
    }

    this.#step = target;
    this.#furthest = Math.max(this.#furthest, target);

    // Mini frames must be clicked by the customer; the background starts on the first value (e.g. White).
    if (target === 3 && !this.#state.background) {
      this.#state.background = this.#backgroundValues()[0]?.value ?? null;
    }

    this.#render();

    const heading = this.querySelector(`[data-step="${target}"] [data-step-heading]`);
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
    if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start' });
  }

  #reset() {
    this.#state = this.#emptyState();
    this.#step = 0;
    this.#furthest = 0;
    this.#setError('');
    this.#render();
    const heading = this.querySelector('[data-step="0"] [data-step-heading]');
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
    this.scrollIntoView({ block: 'start' });
  }

  /* ---------- Rendering ---------- */

  /** @param {{ keepOptions?: boolean }} [options] */
  #render({ keepOptions = false } = {}) {
    if (!keepOptions) {
      this.#renderModels();
      this.#renderValues('colour', this.#colourValues(), this.#state.colour, () => null);
      this.#renderValues('mini', this.#miniValues(), this.#state.mini, () => null, this.#data.labels.miniCardLabel);
      this.#renderBackgrounds();
      this.#renderExtras();
    }
    this.#renderProgress();
    this.#renderSummary();
    this.#renderPriceBars();
    this.#renderBar();
  }

  /** Scrolls just enough that step 1's price bar sits above the sticky bottom bar on short phone screens. */
  #revealPriceBar() {
    const priceBar = this.querySelector('[data-step="0"] [data-price-bar]');
    if (!(priceBar instanceof HTMLElement) || priceBar.hidden) return;

    const bottomBar = this.querySelector('.museum-builder__bar');
    const barTop =
      bottomBar instanceof HTMLElement && getComputedStyle(bottomBar).position === 'sticky'
        ? bottomBar.getBoundingClientRect().top
        : window.innerHeight;
    const overlap = priceBar.getBoundingClientRect().bottom + 12 - barTop;
    if (overlap <= 0) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollBy({ top: overlap, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** Pink price bars in steps 1-5; step 1 only shows its bar once a frame is chosen. */
  #renderPriceBars() {
    const model = this.#model;
    const { labels } = this.#data;
    const total = this.#total();
    const compareAt = this.#compareAtTotal();
    const detail = model ? [model.label, this.#state.colour].filter(Boolean).join(' · ') : '';

    for (const bar of this.querySelectorAll('[data-price-bar]')) {
      if (!(bar instanceof HTMLElement)) continue;
      bar.hidden = !model;
      if (!model) continue;

      const step = Number(bar.closest('[data-step]')?.getAttribute('data-step') ?? 0);
      const label = bar.querySelector('[data-price-bar-label]');
      const detailElement = bar.querySelector('[data-price-bar-detail]');
      const now = bar.querySelector('[data-price-bar-now]');
      const was = bar.querySelector('[data-price-bar-was]');

      if (label) label.textContent = step === 0 ? labels.priceBarStart || '' : labels.priceBarCurrent || '';
      if (detailElement) detailElement.textContent = detail;
      if (now) now.textContent = this.#money(total);
      if (was) was.textContent = compareAt > total ? this.#money(compareAt) : '';
    }
  }

  #renderModels() {
    const container = this.querySelector('[data-options="model"]');
    if (!container) return;
    const { labels } = this.#data;

    container.replaceChildren(
      ...this.#data.models.map((model) => {
        const soldOut = !model.variants.some((variant) => variant.available);
        return this.#card({
          type: 'radio',
          field: 'model',
          value: model.key,
          label: model.label,
          subtitle: model.subtitle || null,
          image: model.image,
          checked: this.#state.model === model.key,
          disabled: soldOut,
          meta: soldOut ? [this.#badge(labels.soldOut || 'Sold out', true)] : [],
          frame: true,
        });
      })
    );
  }

  /**
   * @param {string} field
   * @param {{ value: string, image: string | null }[]} values
   * @param {string | null} selected
   * @param {(entry: any) => string | null} metaText
   * @param {string | null} [cardLabel] Small label shown above each option name.
   */
  #renderValues(field, values, selected, metaText, cardLabel = null) {
    const container = this.querySelector(`[data-options="${field}"]`);
    if (!(container instanceof HTMLElement)) return;
    // Frame colours: 3 columns, or 2 when the product has exactly two colours.
    if (field === 'colour') {
      container.style.setProperty('--museum-builder-columns', values.length === 2 ? '2' : '3');
    }
    // Mini frames are free today: show their value struck through next to the free price.
    const miniValue = field === 'mini' ? this.#miniFramesValue() : 0;
    container.replaceChildren(
      ...values.map((entry) => {
        const text = metaText(entry);
        return this.#card({
          type: 'radio',
          field,
          value: entry.value,
          label: entry.value,
          image: entry.image,
          checked: selected === entry.value,
          meta: text ? [this.#badge(text)] : [],
          cardLabel: cardLabel || null,
          cardPrice: miniValue ? this.#freePrice(miniValue) : null,
        });
      })
    );
  }

  /** Background tiles show no prices; the difference only shows up in the running total. */
  #renderBackgrounds() {
    const container = this.querySelector('[data-options="background"]');
    if (!container) return;
    const { labels } = this.#data;
    const bestsellers = String(labels.bestsellerBackgrounds || '')
      .split(',')
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean);

    container.replaceChildren(
      ...this.#backgroundValues().map((entry) =>
        this.#card({
          type: 'radio',
          field: 'background',
          value: entry.value,
          label: entry.value,
          image: entry.image,
          checked: this.#state.background === entry.value,
          meta: [],
          checkMark: true,
          tile: true,
          cornerBadge: bestsellers.includes(entry.value.trim().toLowerCase()) ? labels.bestseller || null : null,
        })
      )
    );
  }

  #renderExtras() {
    const container = this.querySelector('[data-options="extras"]');
    if (!container) return;
    container.replaceChildren(
      ...this.#data.extras.map((extra) =>
        this.#card({
          type: 'checkbox',
          field: 'extras',
          value: String(extra.id),
          label: extra.label,
          image: extra.image,
          checked: this.#state.extras.has(extra.id),
          meta: [this.#text(`+${this.#money(extra.price)}`)],
          checkMark: true,
          tile: true,
        })
      )
    );
  }

  #renderProgress() {
    const items = this.querySelectorAll('[data-progress-item]');
    items.forEach((item, index) => {
      if (!(item instanceof HTMLElement)) return;
      // Steps before the current one are done; steps after it look upcoming again after going back.
      const state = index === this.#step ? 'current' : index < this.#step ? 'done' : 'upcoming';
      item.dataset.state = state;
      const button = item.querySelector('button');
      if (!button) return;
      button.disabled = index > this.#furthest || index === this.#step;
      if (index === this.#step) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });

    const fill = this.querySelector('[data-progress-fill]');
    if (fill instanceof HTMLElement) {
      fill.style.setProperty('--museum-builder-progress', String((this.#step + 1) / STEP_COUNT));
    }

    this.dataset.currentStep = String(this.#step);
    this.querySelectorAll('[data-step]').forEach((step) => {
      if (step instanceof HTMLElement) step.hidden = Number(step.dataset.step) !== this.#step;
    });
  }

  #renderSummary() {
    const summary = this.querySelector('[data-summary]');
    const model = this.#model;
    const variant = this.#selectedVariant;
    const { labels } = this.#data;
    const stepLabels = labels.steps || [];

    // No prices per item here; only the total below the card.
    if (summary && model && variant) {
      const { colour, mini, background } = this.#state;
      const chosenExtras = this.#data.extras.filter((extra) => this.#state.extras.has(extra.id));
      // "8 Gold Frames" / "11 Gold Frames" show as "Gold frames" (no count); plain colours become "Gold mini frames".
      const miniCount = mini ? mini.replace(/^\s*\d+\s*/, '') : '';
      const miniText = !mini
        ? ''
        : /frame/i.test(miniCount)
          ? miniCount.charAt(0).toUpperCase() + miniCount.slice(1).toLowerCase()
          : `${mini} ${(stepLabels[2] || 'mini frames').toLowerCase()}`;
      const backgroundImage =
        variant.image ?? this.#backgroundValues().find((entry) => entry.value === background)?.image ?? null;

      summary.replaceChildren(
        this.#summaryRow(
          stepLabels[0] || '',
          [model.label, colour, miniText].filter(Boolean).join(' · '),
          (colour ? this.#altImage(model, 'Frame:', colour) : null) ?? model.image ?? null
        ),
        this.#summaryRow(stepLabels[3] || '', background || '', backgroundImage),
        this.#summaryRow(
          stepLabels[4] || '',
          chosenExtras.length ? chosenExtras.map((extra) => extra.label).join(', ') : labels.noExtras || ''
        )
      );
    }

    const reviewTotal = this.querySelector('[data-review-total]');
    if (reviewTotal) reviewTotal.textContent = this.#money(this.#total());

    // "Your free gifts today": the list that belongs to the chosen frame size.
    const giftsList = this.querySelector('[data-gifts-list]');
    const list = model ? this.#data.gifts.listFor?.[model.key] : null;
    if (giftsList instanceof HTMLElement && list) giftsList.textContent = list;
  }

  /**
   * One review row: optional thumbnail, small label and the chosen value.
   * @param {string} label
   * @param {string} value
   * @param {string | null | false} [image] Pass an image URL (or null for a placeholder); omit for no thumbnail.
   */
  #summaryRow(label, value, image = false) {
    const row = document.createElement('li');
    // Rows without a thumbnail (extras) put the label left and the value right on one line.
    row.className = `museum-builder__summary-row${image === false ? ' museum-builder__summary-row--inline' : ''}`;

    if (image !== false) {
      const thumb = document.createElement('span');
      thumb.className = 'museum-builder__summary-thumb';
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.width = 96;
        img.height = 96;
        thumb.append(img);
      } else {
        const placeholder = this.querySelector('template[data-placeholder]');
        if (placeholder instanceof HTMLTemplateElement) thumb.append(placeholder.content.cloneNode(true));
      }
      row.append(thumb);
    }

    const text = document.createElement('span');
    text.className = 'museum-builder__summary-text';
    const labelElement = document.createElement('span');
    labelElement.className = 'museum-builder__summary-label';
    labelElement.textContent = label;
    const valueElement = document.createElement('span');
    valueElement.className = 'museum-builder__summary-value';
    valueElement.textContent = value;
    text.append(labelElement, valueElement);
    row.append(text);

    return row;
  }

  #renderBar() {
    const back = this.querySelector('.museum-builder__bar [data-action="back"]');
    const bar = this.querySelector('.museum-builder__bar');

    // The review step has its own total and checkout button, so the bar is hidden there.
    if (bar instanceof HTMLElement) bar.hidden = this.#step === STEP_COUNT - 1;

    if (back instanceof HTMLButtonElement) back.hidden = this.#step === 0;

    // The bottom bar and step 1's own full-width button both use data-action="next".
    for (const next of this.querySelectorAll('.museum-builder__bar [data-action="next"]')) {
      if (next instanceof HTMLButtonElement) next.hidden = this.#step === STEP_COUNT - 1;
    }
    for (const next of this.querySelectorAll('[data-action="next"]')) {
      if (next instanceof HTMLButtonElement) next.disabled = !this.#canContinue(this.#step);
    }
  }

  /**
   * @param {{ type: 'radio' | 'checkbox', field: string, value: string, label: string, image: string | null,
   *   checked: boolean, disabled?: boolean, meta: Node[], checkMark?: boolean, tile?: boolean,
   *   cornerBadge?: string | null, subtitle?: string | null, frame?: boolean, cardLabel?: string | null,
   *   cardPrice?: Node | null }} options
   */
  #card({
    type,
    field,
    value,
    label,
    image,
    checked,
    disabled = false,
    meta,
    checkMark = false,
    tile = false,
    cornerBadge = null,
    subtitle = null,
    frame = false,
    cardLabel = null,
    cardPrice = null,
  }) {
    const card = document.createElement('label');
    // Steps 1-3 share the bordered option-card style; background tiles have no card, extras keep the plain card.
    const option = field !== 'extras' && !tile;
    card.className = `museum-builder__card${option ? ' museum-builder__card--option' : ''}${
      tile ? ' museum-builder__card--tile' : ''
    }${frame ? ' museum-builder__card--frame' : ''}`;

    const input = document.createElement('input');
    input.type = type;
    input.className = 'museum-builder__card-input';
    input.name = `${this.id}-${field}`;
    input.value = value;
    input.checked = checked;
    input.disabled = disabled;
    input.dataset.field = field;

    const media = document.createElement('span');
    media.className = 'museum-builder__card-media';
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      media.append(img);
    } else {
      const placeholder = this.querySelector('template[data-placeholder]');
      if (placeholder instanceof HTMLTemplateElement) media.append(placeholder.content.cloneNode(true));
    }

    const name = document.createElement('span');
    name.className = 'museum-builder__card-name';
    name.textContent = label;

    card.append(input, media);

    if (cardLabel) {
      const labelElement = document.createElement('span');
      labelElement.className = 'museum-builder__card-label';
      labelElement.textContent = cardLabel;
      card.append(labelElement);
    }

    if (cardPrice) card.append(cardPrice);

    card.append(name);

    if (subtitle) {
      const subtitleElement = document.createElement('span');
      subtitleElement.className = 'museum-builder__card-subtitle';
      subtitleElement.textContent = subtitle;
      card.append(subtitleElement);
    }

    if (meta.length) {
      const metaWrapper = document.createElement('span');
      metaWrapper.className = 'museum-builder__card-meta';
      metaWrapper.append(...meta);
      card.append(metaWrapper);
    }

    if (cornerBadge) {
      const badge = document.createElement('span');
      badge.className = 'museum-builder__corner-badge';
      badge.textContent = cornerBadge;
      media.append(badge);
    }

    if (checkMark) {
      const check = document.createElement('span');
      check.className = 'museum-builder__check';
      check.setAttribute('aria-hidden', 'true');
      // Tiles keep the check inside the image so it sits on the tile's corner.
      (tile ? media : card).append(check);
    }

    return card;
  }

  /** @param {string} text @param {boolean} [soldOut] */
  #badge(text, soldOut = false) {
    const badge = document.createElement('span');
    badge.className = `museum-builder__badge${soldOut ? ' museum-builder__badge--sold-out' : ''}`;
    badge.textContent = text;
    return badge;
  }

  /**
   * "<s>€7,95</s> €0,00" line on the mini frames cards.
   * @param {number} cents
   */
  #freePrice(cents) {
    const price = document.createElement('span');
    price.className = 'museum-builder__card-price';
    const was = document.createElement('s');
    was.textContent = this.#money(cents);
    const now = document.createElement('strong');
    now.textContent = this.#money(0);
    price.append(was, now);
    return price;
  }

  /** @param {string} text */
  #text(text) {
    const span = document.createElement('span');
    span.textContent = text;
    return span;
  }

  /** @param {string} message */
  #setError(message) {
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /* ---------- Cart ---------- */

  /** @param {HTMLButtonElement} button */
  async #checkout(button) {
    const variant = this.#selectedVariant;
    if (!variant || button.getAttribute('aria-busy') === 'true') return;

    const items = [{ id: variant.id, quantity: 1 }, ...[...this.#state.extras].map((id) => ({ id, quantity: 1 }))];

    // Free gifts (EUR 0.00 products), listed as their own lines: mini figures + lights, and the chosen mini frames.
    const { figures, frames } = this.#data.gifts;
    const mini = String(this.#state.mini || '').trim().toLowerCase();
    const frame = (frames || []).find((entry) => entry.value.trim().toLowerCase() === mini) ?? this.#giftFrameFor(this.#model);
    if (frame) items.push({ id: frame.id, quantity: 1 });
    if (figures) items.push({ id: figures.id, quantity: 1 });

    this.#setError('');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || '');
      }

      window.location.href = this.dataset.checkoutUrl || '/checkout';
    } catch (error) {
      const detail = error instanceof Error && error.message ? ` (${error.message})` : '';
      this.#setError(`${this.#data.labels.error || 'Something went wrong.'}${detail}`);
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('museum-builder')) {
  customElements.define('museum-builder', MuseumBuilder);
}

const CAROUSEL_INTERVAL = 4000;
const CAROUSEL_VISIBLE = 2;

/**
 * Photo and video carousel below the builder reviews. Shows two slides side by side and moves on
 * by one slide every 4 seconds, looping endlessly by cloning the first slides at the end.
 * Videos are muted autoplay loops and are never paused when they slide out of view.
 */
class MuseumCarousel extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {number | undefined} */
  #timer;
  #index = 0;
  #count = 0;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const track = this.#track;
    if (!track) return;

    track.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
    const slides = [...track.querySelectorAll('[data-carousel-slide]')];
    this.#count = slides.length;
    this.#index = 0;
    track.scrollLeft = 0;
    this.#playVideos(track);

    // Nothing to slide when everything already fits.
    if (this.#count <= CAROUSEL_VISIBLE) return;

    for (const slide of slides.slice(0, CAROUSEL_VISIBLE)) {
      const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
      clone.dataset.carouselClone = '';
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      track.append(clone);
    }
    this.#playVideos(track);

    // A visitor swiping or touching pauses the autoplay; it picks up again afterwards.
    for (const type of ['pointerdown', 'wheel', 'touchstart']) {
      track.addEventListener(type, () => this.#restart(), { signal, passive: true });
    }
    track.addEventListener(
      'scrollend',
      () => {
        this.#index = Math.round(track.scrollLeft / this.#step);
        if (this.#index >= this.#count) this.#jumpToStart();
      },
      { signal }
    );
    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.#stop() : this.#restart()),
      { signal }
    );

    this.#restart();
  }

  disconnectedCallback() {
    this.#stop();
    this.#abort?.abort();
  }

  get #track() {
    const track = this.querySelector('[data-carousel-track]');
    return track instanceof HTMLElement ? track : null;
  }

  /** Width of one slide plus the gap. */
  get #step() {
    const track = this.#track;
    const slide = track?.querySelector('[data-carousel-slide]');
    if (!track || !(slide instanceof HTMLElement)) return 1;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap || 1;
  }

  /** @param {HTMLElement} track */
  #playVideos(track) {
    for (const video of track.querySelectorAll('video')) {
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      video.play().catch(() => {});
    }
  }

  #next() {
    const track = this.#track;
    if (!track) return;
    this.#index += 1;
    // With reduced motion the carousel still moves on, but steps instead of gliding.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: this.#index * this.#step, behavior: reduceMotion ? 'instant' : 'smooth' });
    // Back to the start once the clones are reached; also covers browsers without scrollend.
    if (this.#index >= this.#count) {
      window.setTimeout(() => this.#jumpToStart(), reduceMotion ? 50 : 700);
    }
  }

  /** The clones look exactly like the first slides, so jumping back is invisible. */
  #jumpToStart() {
    const track = this.#track;
    if (!track) return;
    this.#index = 0;
    track.scrollTo({ left: 0, behavior: 'instant' });
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #restart() {
    this.#stop();
    this.#timer = window.setInterval(() => this.#next(), CAROUSEL_INTERVAL);
  }
}

if (!customElements.get('museum-carousel')) {
  customElements.define('museum-carousel', MuseumCarousel);
}
